import { motion, MotionConfig } from "framer-motion";
import {
  Check,
  UserCircle,
  School,
  GraduationCap,
  LayoutDashboard,
  ShieldCheck,
} from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import { Card } from "../../../components/ui/card";
import { useInViewOnce } from "../../../hooks/useInViewOnce";

const SPRING: [number, number, number, number] = [0.34, 1.56, 0.64, 1];

const PERSONAS = [
  {
    icon: UserCircle,
    title: "Estudante",
    tagline: "Modo livre — personalização real",
    accent: "from-cyan-500/20 to-sky-500/10 border-cyan-400/30",
    iconBg: "from-cyan-400 to-sky-500",
    bullets: [
      "Questionário vira perfil estruturado (não é decorativo)",
      "Mesmo conteúdo base, apresentação e ritmo ajustados ao perfil",
      "Plano de estudo, revisão e gamificação para engajamento",
      "Pontos e rankings não substituem nota formal",
    ],
  },
  {
    icon: School,
    title: "Aluno institucional",
    tagline: "Vínculo com a escola — sem ficar bloqueado",
    accent: "from-sky-500/20 to-blue-500/10 border-sky-400/30",
    iconBg: "from-sky-400 to-blue-600",
    bullets: [
      "Matrícula e turma; status pendente até confirmar vínculo",
      "Enquanto isso, usa o fluxo de estudante normalmente",
      "Dashboard institucional como foco após vinculação (roadmap)",
      "Família acompanha prazos e desempenho pelo painel institucional",
    ],
  },
  {
    icon: GraduationCap,
    title: "Professor",
    tagline: "Conteúdo, atividades e correção",
    accent: "from-emerald-500/20 to-teal-500/10 border-emerald-400/30",
    iconBg: "from-emerald-400 to-teal-500",
    bullets: [
      "Salas, módulos, prazos e regras de progressão (roadmap)",
      "Atividades objetivas e discursivas com correção assistida",
      "Materiais centralizados; simulados personalizados quando disponível",
      "Notas e feedback na camada avaliativa — separada da gamificação",
    ],
  },
  {
    icon: LayoutDashboard,
    title: "Coordenador",
    tagline: "Gestão com dados, não com feeling",
    accent: "from-violet-500/20 to-fuchsia-500/10 border-violet-400/30",
    iconBg: "from-violet-400 to-fuchsia-500",
    bullets: [
      "Visão por turma, disciplina e professor",
      "Importação em lote e relatórios (roadmap)",
      "Métricas institucionais e saúde operacional",
      "Professor, coordenação e admin como papéis distintos",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Admin",
    tagline: "Governança da plataforma",
    accent: "from-slate-500/25 to-zinc-500/10 border-slate-400/25",
    iconBg: "from-slate-500 to-zinc-600",
    bullets: [
      "Instituições e políticas globais do sistema",
      "Auditoria e estrutura macro multi-tenant",
      "Regras que escalam sem misturar papéis pedagógicos",
      "Ideal para operações e TI educacional",
    ],
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 32 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: SPRING, delay: i * 0.09 },
  }),
};

export function PersonasSection() {
  const { ref, isVisible } = useInViewOnce();

  return (
    <MotionConfig reducedMotion="user">
      <section
        id="ecossistema"
        ref={ref}
        className="scroll-mt-20 border-b border-white/[0.06] py-16 sm:scroll-mt-24 sm:py-20 lg:py-24"
      >
        <div className="container mx-auto max-w-7xl px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: [0, 0, 0.2, 1] }}
            className="mx-auto max-w-3xl text-center"
          >
            <Badge className="mb-4 border-cyan-500/30 bg-cyan-500/10 text-cyan-200">
              Ecossistema
            </Badge>
            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl md:text-4xl">
              Cinco papéis claros.{" "}
              <span className="bg-gradient-to-r from-teal-300 to-cyan-300 bg-clip-text text-transparent">
                Duas camadas: aprender e avaliar.
              </span>
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-400 sm:text-lg">
              Estudante, aluno institucional, professor, coordenador e admin — sem
              misturar "funcionário genérico". Gamificação e estudo personalizado não
              substituem nota formal.
            </p>
          </motion.div>

          <div className="mx-auto mt-12 grid max-w-6xl gap-4 sm:mt-16 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
            {PERSONAS.map((p, i) => (
              <motion.div
                key={p.title}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                animate={isVisible ? "visible" : "hidden"}
              >
                <Card
                  className={`card-lift h-full border bg-gradient-to-br p-5 backdrop-blur-md sm:p-6 ${p.accent}`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br shadow-lg ${p.iconBg}`}
                    >
                      <p.icon className="h-6 w-6 text-white" aria-hidden />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-display text-xl font-semibold text-white">
                        {p.title}
                      </h3>
                      <p className="text-sm text-teal-200/80">{p.tagline}</p>
                      <ul className="mt-4 space-y-2.5">
                        {p.bullets.map((b) => (
                          <li key={b} className="flex gap-2 text-sm text-slate-300">
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-teal-400" aria-hidden />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </MotionConfig>
  );
}
